import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Minus, Trophy } from 'lucide-react';
import { liveGameService } from '@/services/liveGameService';
import { toast } from 'sonner';

interface LiveGame {
  id: string;
  game_title: string;
  game_date: string;
  game_time: string;
  home_team_score?: number;
  away_team_score?: number;
  current_period?: string;
  game_clock?: string;
  sport_id: number;
}

const LiveScoring: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const [game, setGame] = useState<LiveGame | null>(null);
  const [homeScore, setHomeScore] = useState(0);
  const [awayScore, setAwayScore] = useState(0);
  const [currentPeriod, setCurrentPeriod] = useState('1st Quarter');
  const [gameClock, setGameClock] = useState('12:00');
  const [selectedPlayer, setSelectedPlayer] = useState('');
  const [statType, setStatType] = useState('');
  const [statValue, setStatValue] = useState(1);
  const [loading, setLoading] = useState(true);

  const currentUser = user || internalUser;

  const periods = ['1st Quarter', '2nd Quarter', '3rd Quarter', '4th Quarter'];
  const basketballStats = ['points', 'assists', 'rebounds', 'fouls', 'turnovers'];

  useEffect(() => {
    if (gameId && currentUser) {
      loadGame();
    }
  }, [gameId, currentUser]);

  const loadGame = async () => {
    if (!gameId) return;

    try {
      const { data, error } = await supabase
        .from('venue_games')
        .select('*')
        .eq('id', gameId)
        .single();

      if (error) {
        console.error('Error loading game:', error);
        toast.error('Failed to load game');
        navigate('/scorekeeper/assignments');
        return;
      }

      const gameData: LiveGame = {
        id: data.id,
        game_title: data.game_title,
        game_date: data.game_date,
        game_time: data.game_time,
        sport_id: data.sport_id || 1,
        home_team_score: 0,
        away_team_score: 0,
        current_period: '1st Quarter',
        game_clock: '12:00'
      };

      setGame(gameData);
    } catch (error) {
      console.error('Error loading game:', error);
      toast.error('Failed to load game');
    } finally {
      setLoading(false);
    }
  };

  const updateScore = (team: 'home' | 'away', change: number) => {
    if (team === 'home') {
      setHomeScore(prev => Math.max(0, prev + change));
    } else {
      setAwayScore(prev => Math.max(0, prev + change));
    }
    toast.success(`${team === 'home' ? 'Home' : 'Away'} team score updated`);
  };

  const recordPlayerStat = async () => {
    if (!selectedPlayer || !statType || !currentUser || !gameId) {
      toast.error('Please fill in all fields');
      return;
    }

    const success = await liveGameService.recordPlayerStat(
      gameId,
      selectedPlayer,
      game?.sport_id || 1,
      statType,
      statValue,
      currentUser.id
    );

    if (success) {
      toast.success('Player stat recorded successfully');
      setSelectedPlayer('');
      setStatType('');
      setStatValue(1);
    } else {
      toast.error('Failed to record player stat');
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Loading scoring session...</div>
        </div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Game Not Found</h1>
          <p className="text-muted-foreground mb-4">The game could not be found.</p>
          <Button onClick={() => navigate('/scorekeeper/assignments')}>
            Back to Assignments
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Trophy className="h-8 w-8 text-blue-600" />
            Live Scoring: {game.game_title}
          </h1>
        </div>
        <Badge variant="outline" className="text-lg px-4 py-2">
          ACTIVE
        </Badge>
      </div>

      {/* Score Display */}
      <Card>
        <CardHeader>
          <CardTitle className="text-center">Game Score</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-around text-center">
            <div>
              <h3 className="text-lg font-semibold mb-2">Home Team</h3>
              <div className="text-6xl font-bold mb-4">{homeScore}</div>
              <div className="flex gap-2 justify-center">
                <Button
                  onClick={() => updateScore('home', 1)}
                  className="flex items-center gap-1"
                >
                  <Plus className="h-4 w-4" /> 1
                </Button>
                <Button
                  onClick={() => updateScore('home', -1)}
                  variant="outline"
                  className="flex items-center gap-1"
                >
                  <Minus className="h-4 w-4" /> 1
                </Button>
              </div>
            </div>
            
            <div className="text-center">
              <div className="text-2xl font-bold mb-2">VS</div>
              <div className="space-y-2">
                <div>
                  <Label htmlFor="period">Period:</Label>
                  <Select value={currentPeriod} onValueChange={setCurrentPeriod}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select period" />
                    </SelectTrigger>
                    <SelectContent>
                      {periods.map((period) => (
                        <SelectItem key={period} value={period}>
                          {period}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="clock">Game Clock:</Label>
                  <Input
                    id="clock"
                    value={gameClock}
                    onChange={(e) => setGameClock(e.target.value)}
                    placeholder="00:00"
                    className="text-center"
                  />
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2">Away Team</h3>
              <div className="text-6xl font-bold mb-4">{awayScore}</div>
              <div className="flex gap-2 justify-center">
                <Button
                  onClick={() => updateScore('away', 1)}
                  className="flex items-center gap-1"
                >
                  <Plus className="h-4 w-4" /> 1
                </Button>
                <Button
                  onClick={() => updateScore('away', -1)}
                  variant="outline"
                  className="flex items-center gap-1"
                >
                  <Minus className="h-4 w-4" /> 1
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Player Stats Entry */}
      <Card>
        <CardHeader>
          <CardTitle>Player Statistics Entry</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="playerSelect">Player ID:</Label>
              <Input
                id="playerSelect"
                value={selectedPlayer}
                onChange={(e) => setSelectedPlayer(e.target.value)}
                placeholder="Enter player ID"
              />
            </div>
            <div>
              <Label htmlFor="statType">Statistic Type:</Label>
              <Select value={statType} onValueChange={setStatType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select stat type" />
                </SelectTrigger>
                <SelectContent>
                  {basketballStats.map((stat) => (
                    <SelectItem key={stat} value={stat}>
                      {stat.charAt(0).toUpperCase() + stat.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="statValue">Value:</Label>
              <Input
                id="statValue"
                type="number"
                value={statValue}
                onChange={(e) => setStatValue(parseInt(e.target.value) || 1)}
                min="1"
              />
            </div>
            <div className="flex items-end">
              <Button onClick={recordPlayerStat} className="w-full">
                Record Stat
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LiveScoring;